import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminApi, mediaUrl } from "../api";
import { AField, Btn, Modal, PageHead, Toggle, Uploader, inputCls } from "../components";

const EMPTY = { headline: "", subtext: "", cta_label: "", cta_url: "", artwork: "", active: false };

export default function Featured() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/featured-media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (f) => {
    setForm({ headline: f.headline, subtext: f.subtext || "", cta_label: f.cta_label || "", cta_url: f.cta_url || "", artwork: f.artwork || "", active: !!f.active });
    setEditing(f.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await adminApi.post("/admin/featured-media", form);
      else await adminApi.put(`/admin/featured-media/${editing}`, form);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm("Delete this banner?")) return;
    await adminApi.delete(`/admin/featured-media/${f.id}`);
    load();
  };

  return (
    <div data-testid="admin-featured">
      <PageHead title="FEATURED MEDIA" sub="The homepage takeover banner. One active at a time — activating one deactivates the rest.">
        <Btn onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="featured-new">
          <Plus size={13} /> NEW BANNER
        </Btn>
      </PageHead>

      <div className="grid md:grid-cols-2 gap-4" data-testid="featured-list">
        {items.length === 0 && (
          <p className="p-8 font-mono text-xs text-clay text-center border border-woodborder md:col-span-2" data-testid="featured-empty">
            NO CUSTOM BANNER ACTIVE — THE DEFAULT COBAIN BANNER IS SHOWING ON THE SITE.
          </p>
        )}
        {items.map((f, i) => (
          <div key={f.id} className={`border p-5 ${f.active ? "border-gold/50 bg-gold/5" : "border-woodborder bg-card/40"}`} data-testid={`featured-row-${i}`}>
            {f.artwork && <img src={mediaUrl(f.artwork)} alt="" className="w-full aspect-video object-cover border border-woodborder mb-4" />}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-editorial font-bold text-cream">{f.headline}</p>
              {f.active && <span className="font-mono text-[9px] tracking-[0.2em] bg-gold text-obsidian font-bold px-2.5 py-1 rounded-full">LIVE</span>}
            </div>
            {f.subtext && <p className="mt-1.5 text-sand text-sm font-light line-clamp-2">{f.subtext}</p>}
            {f.cta_label && <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-tan">CTA: {f.cta_label} → {f.cta_url || "—"}</p>}
            <div className="mt-4 flex gap-2">
              <Btn variant="ghost" onClick={() => openEdit(f)} data-testid={`featured-edit-${i}`}><Pencil size={12} /> EDIT</Btn>
              <Btn variant="danger" onClick={() => remove(f)} data-testid={`featured-delete-${i}`}><Trash2 size={12} /> DELETE</Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "NEW BANNER" : "EDIT BANNER"} wide>
        <div className="space-y-5" data-testid="featured-editor">
          <AField label="BANNER ARTWORK">
            <div className="flex items-start gap-4 flex-wrap">
              <Uploader onUploaded={(p) => setForm({ ...form, artwork: p })} accept="image/*" label="UPLOAD ARTWORK" testId="featured-artwork-upload" />
              {form.artwork && <img src={mediaUrl(form.artwork)} alt="" className="w-40 aspect-video object-cover border border-woodborder" data-testid="featured-artwork-preview" />}
            </div>
          </AField>
          <AField label="HEADLINE *"><input className={inputCls} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="featured-headline" /></AField>
          <AField label="SUPPORTING TEXT"><textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.subtext} onChange={(e) => setForm({ ...form, subtext: e.target.value })} data-testid="featured-subtext" /></AField>
          <div className="grid sm:grid-cols-2 gap-5">
            <AField label="CTA LABEL"><input className={inputCls} value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="WATCH NOW" data-testid="featured-cta-label" /></AField>
            <AField label="CTA DESTINATION URL"><input className={inputCls} value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="https://" data-testid="featured-cta-url" /></AField>
          </div>
          <Toggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="ACTIVATE ON HOMEPAGE" testId="featured-active" />
          <div className="flex gap-3 pt-2 border-t border-woodborder">
            <Btn variant="gold" disabled={saving || !form.headline.trim()} onClick={save} data-testid="featured-save">{saving ? "SAVING..." : "SAVE BANNER"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
